<template>
  <panel-layout
    id="panel"
    :loading="loading"
    :heading="heading"
  >
    <template v-if="waitlist" #actions-right>
      <o-tooltip v-if="hasAvailability" :text="$t('customer.notify')">
        <o-button flat icon="notification" @click="handleNotify" />
      </o-tooltip>
      <o-tooltip :text="$t('pages.waitlist.id.resend')">
        <o-button flat icon="email" @click="handleEmail" />
      </o-tooltip>
      <o-tooltip :text="$t('general.cancel')">
        <o-button flat icon="cancel" @click="handleCancel" />
      </o-tooltip>
      <o-tooltip :text="$t('general.delete')">
        <o-button flat icon="delete" @click="handleDelete" />
      </o-tooltip>
    </template>

    <template #info>
      <o-chip v-if="waitlist" :variant="waitlist.statusColour" flat>
        {{ $t(WaitlistStatusLabels[waitlist.status as WaitlistStatus]) }}
      </o-chip>
    </template>

    <div class="h-full flex flex-col">
      <o-scroll v-if="waitlist" class="p-6">
        <o-alert variant="info" show :title="$t('waitlist.request')" class="mb-8">
          <div class="text-sm">
            <p>{{ $t('pages.waitlist.id.requested', { name: customer.displayName }) }}</p>
            <ul class="list-disc ml-2 pl-2">
              <li v-for="service in waitlist.services" :key="service.service.id">
                {{ service.service.title }}
              </li>
            </ul>
            <template v-if="waitlist.dates.length">
              <p>{{ $t('pages.waitlist.id.onTheseDates') }}</p>
              <ul class="list-disc ml-2 pl-2">
                <li v-for="(date, index) in waitlist.datesLabel" :key="index">
                  {{ date }}
                </li>
              </ul>
            </template>
          </div>
        </o-alert>

        <p class="text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
          {{ $t('general.customer') }}
        </p>
        <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border mb-8">
          <customer-preview v-if="$grants.all({ customer: ['profile']})" :customer="waitlist.customer" />
          <p v-else-if="waitlist.customer">
            {{ customer.displayName }}
          </p>
        </div>


        <p class="text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
          {{ $t('pages.waitlist.id.requestedServices') }}
        </p>
        <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border flex flex-col divide-x space-y-3 mb-8">
          <div
            v-for="service in waitlist.services"
            :key="`${waitlist.idWaitlist}-${service.idService}`"
            class="flex items-center last:mb-0"
          >
            <div
              class="self-stretch w-2 mr-2 rounded"
              :style="{
                backgroundColor: '#' + service.service.borderHexColour
              }"
            />
            <div class="w-full">
              <p class="text-sm text-gray-800 dark:text-gray-100">
                {{ service.service.title }}
              </p>
              <p class="text-sm text-gray-700 dark:text-gray-200 flex flex-wrap">
                <span v-if="service.employee && service.employee.displayName" class="mr-4">
                  {{ service.employee.displayName }}
                </span>
                <span v-if="!service.employee" class="mr-4">
                  {{ $t('service.firstAvailable') }}
                </span>
              </p>
            </div>
          </div>
        </div>


        <template v-if="availability && availability.firstSlot">
          <p class="text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
            {{ $t('pages.waitlist.id.firstAvailable') }}
          </p>
          <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border">
            <p class="mb-2 font-semibold text-gray-800 dark:text-gray-100">
              {{ $date.format(availability.firstSlot.date * 1000, $date.presets.long) }}
            </p>
            <div
              v-for="service in availability.firstSlot.services"
              :key="service.idService"
              class="flex items-center mb-3 rounded last:mb-0"
            >
              <div
                class="self-stretch w-2 mr-2 rounded"
                :style="{
                  backgroundColor: '#' + service.service.borderHexColour
                }"
              />
              <div class="flex-shrink-0 text-gray-700 dark:text-gray-200 font-semibold mr-4 text-sm">
                <p>
                  {{ $date.minutes(service.startTime) }}
                </p>
                <p data-cy="finish-time">
                  {{ $date.minutes(service.startTime + service.duration) }}
                </p>
              </div>
              <div class="w-full">
                <p class="text-gray-800 dark:text-gray-100 text-sm">
                  {{ service.service.title }}
                </p>

                <p class="text-sm text-gray-700 dark:text-gray-200 flex flex-wrap">
                  <span class="mr-4">
                    {{ $t('datetime.increments.mins', { mins: service.duration }, Number(service.duration)) }}
                  </span>
                  <span v-if="service.employee && service.employee.displayName" class="mr-4">
                    {{ service.employee.displayName }}
                  </span>
                  <span class="mr-4">
                    {{ service.priceLabel }}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <p class="text-center mt-4">
            <o-panel-link
              class="text-sm text-gray-600 dark:text-gray-300 text-center mt-2 mb-6 mx-auto hover:text-blue-600"
              path="/appointment/create"
              tag="a"
              :props="props"
            >
              {{ $t('pages.waitlist.id.more', { label: availability.totalSlots }) }}
            </o-panel-link>
          </p>
        </template>

        <template v-if="reservation">
          <p class="text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
            {{ $t('pages.waitlist.id.booked') }}
          </p>
          <o-panel-link
            :path="`/appointment/${reservation.idReservation}?date=${reservation.date}`"
            class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border block"
          >
            <p>{{ reservation.customerInfoLabel }}</p>
          </o-panel-link>
        </template>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="waitlist">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="handleClose">
              {{ $t('general.close') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-panel-link
              tag="o-button"
              path="/appointment/create"
              variant="success"
              class="flex-shrink-0"
              :props="props"
            >
              {{ $t('reservation.new') }}
            </o-panel-link>
          </div>
        </div>
      </div>
    </template>

    <email-dialog
      ref="emailDialog"
      :portal="panel.portal"
    />

    <notify-dialog
      ref="notifyDialog"
      :portal="panel.portal"
    />

    <cancel-dialog
      ref="cancelDialog"
      :portal="panel.portal"
      @update:waitlist="handleWaitlistUpdate"
    />

    <delete-dialog
      ref="deleteDialog"
      :portal="panel.portal"
      @success="handleClose"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import EmailDialog from '@/components/waitlist/actions/Email.vue'
// @ts-ignore
import NotifyDialog from '@/components/waitlist/actions/Notify.vue'
// @ts-ignore
import CancelDialog from '@/components/waitlist/actions/Cancel.vue'
// @ts-ignore
import DeleteDialog from '@/components/waitlist/actions/Delete.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import CustomerPreview from '@/components/customer/elements/Preview.vue'

// @ts-ignore
import {
  ODescriptionDetails,
  OAvatar,
  OAlert,
} from '@/components/elements'

import { WaitlistStatus, WaitlistStatusLabels } from '~/_api/models/waitlist'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const componentProps = defineProps<{ id?: string }>()

const { $api, $notification } = useNuxtApp()

const emailDialog = ref<any>()
const notifyDialog = ref<any>()
const cancelDialog = ref<any>()
const deleteDialog = ref<any>()

const waitlist = ref<any>(null)
const loading = ref(false)

const heading = computed(() => 'Waitlist')
const customer = computed(() => waitlist.value?.customer ?? null)
const services = computed(() => waitlist.value?.services ?? [])
const availability = computed(() => waitlist.value?.availability ?? null)
const reservation = computed(() => waitlist.value?.reservation ?? null)

// Named `props` to match template `:props="props"` binding
const props = computed(() => {
  const w = waitlist.value
  const c = w?.customer ?? null

  return {
    presets: {
      waitlist: w,
      customer: c,
    },
  }
})

const hasAvailability = computed(() => waitlist.value?.hasAvailability ?? false)
const isCancelled = computed(() => waitlist.value?.isCancelled ?? false)
const isExpired = computed(() => waitlist.value?.status === WaitlistStatus.expired || waitlist.value?.isExpired === true)
const isBooked = computed(() => waitlist.value?.isBooked ?? false)
const isWaiting = computed(() => waitlist.value?.isWaiting ?? false)

const fetchWaitlist = async (idWaitlist?: string) => {
  loading.value = true

  try {
    if (idWaitlist) {
      const w = (await $api.waitlist.get(idWaitlist)).data
      if (w) {
        waitlist.value = w
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleNotify = () => {
  notifyDialog.value.open(waitlist.value)
}

const handleEmail = () => {
  emailDialog.value.open(waitlist.value)
}

const handleCancel = () => {
  cancelDialog.value.open(waitlist.value)
}

const handleWaitlistUpdate = (updatedWaitlist: any) => {
  waitlist.value = updatedWaitlist
}

const handleDelete = () => {
  deleteDialog.value.open(waitlist.value)
}

const handleClose = () => {
  panel.close()
}

watch(() => componentProps.id, (id) => fetchWaitlist(id), { immediate: true })
</script>
